import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FormattedHTMLMessage, FormattedMessage, injectIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';
import axios from 'axios';
import { Errors } from '../../common';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import app from '../../app';
import * as Yup from 'yup';
import attachedFile from '../../attachedFile';
import geometricElements from '..';
import { getServiceToken } from '../../../backend/appFetch';
import { generateIdentificatorForLoadingAction } from '../../app/util';
import * as FilePond from 'react-filepond';
import { importGometricElementsCompleted } from '../actions';

const mapStateToProps = function (state) {
    return ({
        loading: app.selectors.isLoading(state),
        allFileFormat: attachedFile.selectors.getAllFileFormat(state)
    });
}

const ImportGeometricElementsFromShape = ({ modalShow, onClose, id, loading, ...props }) => {

    const dispatch = useDispatch();

    const Schema = Yup.object().shape({
        strategy: Yup.string().required(<FormattedMessage
            id="project.app.Body.field.required"
            values={{
                field: <FormattedMessage id="project.elements.import.strategy" />,
            }} />),
        includeLog: Yup.string().required(<FormattedMessage
            id="project.app.Body.field.required"
            values={{
                field: <FormattedMessage id="project.elements.import.includeLog" />,
            }} />),
        shapefile: Yup.mixed().required(<FormattedMessage
            id="project.app.Body.field.required"
            values={{
                field: <FormattedMessage id="project.elements.import.shapefile" />,
            }} />).test('isZip', <FormattedMessage id="project.elements.import.shapefile.mustBeZip" />, value => {

                if (!value) {

                    return false;
                }

                let fileContentType = value.type;
                let validFormats = props.allFileFormat.filter(fileFormat => fileFormat.code === 'FILE_FORMAT_ZIP')
                    .filter(fileFormat =>
                        fileFormat.mainMimeType === fileContentType || (fileFormat.additionalMimeType && fileFormat.additionalMimeType.includes(
                            fileContentType
                        )));

                return validFormats.length > 0;
            })
    })

    const [backendErrors, setBackendErrors] = useState(null);
    const [generalAttachedFile, setGeneralAttachedFile] = useState(null);
    const [importErrors, setImportErrors] = useState(null);
    const [fileDownloadError, setFileDownloadError] = useState(false);

    const closeModalWindow = (resetForm) => {

        if (generalAttachedFile) {

            dispatch(importGometricElementsCompleted(generalAttachedFile));
        }
        onClose();
        setBackendErrors(null);
        setGeneralAttachedFile(null);
        setFileDownloadError(false);
        resetForm();
    }

    let initialValues = {
        shapefile: null,
        strategy: 'IMPORT',
        includeLog: false
    }

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Schema}
            onSubmit={(values, { setSubmitting, resetForm }) => {
                setBackendErrors(null);

                let formData = new FormData();
                formData.append("importErrorStrategy", values.strategy);
                formData.append("shapefile", values.shapefile)
                formData.append("geometricElementTypeId", id);
                formData.append("includeLog", values.includeLog);
                dispatch(geometricElements.actions.importGometricElements(formData,
                    (importResult) => {
                        resetForm({
                            values: initialValues
                        })
                        setGeneralAttachedFile(importResult.generalAttachedFile);
                        setImportErrors(importResult.importErrors);
                    }, (errors) => {
                        setBackendErrors(errors);
                    }));
                setSubmitting(false);
            }}
        >
            {({ values, errors, setFieldValue, setFieldError, resetForm }) =>
                <Modal backdrop="static" show={modalShow} onHide={() => closeModalWindow(resetForm)} scrollable={true} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <FormattedMessage id="project.elements.import.title" />
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form className="needs-validation novalidate" id="importFromShapeForm">

                            <Errors errors={backendErrors} onClose={() => setBackendErrors(null)} />

                            {fileDownloadError && generalAttachedFile && <div className="alert alert-danger" role="alert">
                                <FormattedHTMLMessage id="project.elements.import.report.download.error" />
                            </div>}

                            {!fileDownloadError && !generalAttachedFile && <div className={`alert alert-info`} role="alert">
                                <FormattedHTMLMessage id="project.elements.import.warning.message" />
                            </div>}

                            {!importErrors && !fileDownloadError && generalAttachedFile && <div className="alert alert-success" role="alert">
                                <FormattedHTMLMessage id="project.elements.import.report.download.confirmation" />
                            </div>}

                            {importErrors && !fileDownloadError && generalAttachedFile && <div className="alert alert-warning" role="alert">
                                <FormattedHTMLMessage id="project.elements.import.report.download.warning" />
                            </div>}

                            {!fileDownloadError && !generalAttachedFile &&
                                <>
                                    <div className="form-group">
                                        <label className="font-weight-bold required" htmlFor="file">
                                            <FormattedMessage id="project.elements.import.shapefile" />
                                        </label>
                                        <FilePond.FilePond
                                            id="shapefile"
                                            instantUpload={false}
                                            allowMultiple={false}
                                            labelIdle={props.intl.formatMessage({ id: 'project.common.filepond.labelIdle' })}
                                            labelTapToCancel={props.intl.formatMessage({ id: 'project.common.filepond.labelTapCancel' })}
                                            labelFileLoading={props.intl.formatMessage({ id: 'project.common.filepond.labelLoading' })}
                                            labelFileWaitingForSize={props.intl.formatMessage({ id: 'project.common.filepond.labelWaitForSize' })}
                                            onremovefile={(error, file) => {
                                                setFieldValue("shapefile", null);
                                            }}
                                            onaddfile={(error, file) => {
                                                setFieldValue("shapefile", file.file)
                                            }}
                                        />
                                    </div>
                                    <ErrorMessage name="shapefile" render={(msg) =>
                                        <div id='shapefileError' className="alert alert-danger alert-dismissible fade show" role="alert"
                                            hidden={typeof errors.shapefile === "undefined"}
                                        >
                                            <button type="button" className="close" aria-label="Close"
                                                onClick={() => setFieldError("shapefile", undefined)}
                                            >
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            {msg}
                                        </div>
                                    }
                                    />

                                    <div className="form-group">
                                        <label className="font-weight-bold required" htmlFor="strategy">
                                            <FormattedMessage id="project.elements.import.strategy" />
                                        </label>
                                        <Field as="select" id="strategy" name="strategy" className="form-control">
                                            <option value="IMPORT">
                                                {props.intl.formatMessage({ id: "project.elements.import.strategy.import" })}
                                            </option>
                                            <option value="ABORT">
                                                {props.intl.formatMessage({ id: "project.elements.import.strategy.abort" })}
                                            </option>
                                        </Field>
                                    </div>
                                    <ErrorMessage name="strategy" render={(msg) =>
                                        <div id='strategyError' className="alert alert-danger alert-dismissible fade show" role="alert"
                                            hidden={typeof errors.strategy === "undefined"}
                                        >
                                            <button type="button" className="close" aria-label="Close"
                                                onClick={() => setFieldError("strategy", undefined)}
                                            >
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            {msg}
                                        </div>
                                    }
                                    />

                                    <div className="form-group">
                                        <label className="font-weight-bold required" htmlFor="strategy">
                                            <FormattedMessage id="project.elements.import.includeLog" />
                                        </label>
                                        <Field as="select" id="includeLog" name="includeLog" className="form-control">
                                            <option value="false">
                                                {props.intl.formatMessage({ id: 'project.common.no' })}
                                            </option>
                                            <option value="true">
                                                {props.intl.formatMessage({ id: 'project.common.yes' })}
                                            </option>
                                        </Field>
                                    </div>
                                    <ErrorMessage name="includeLog" render={(msg) =>
                                        <div id='includeLogError' className="alert alert-danger alert-dismissible fade show" role="alert"
                                            hidden={typeof errors.includeLog === "undefined"}
                                        >
                                            <button type="button" className="close" aria-label="Close"
                                                onClick={() => setFieldError("includeLog", undefined)}
                                            >
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                            {msg}
                                        </div>
                                    }
                                    />
                                </>}
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        {!fileDownloadError && generalAttachedFile &&
                            <button
                                id="downloadButton"
                                className='btn btn-primary'
                                onClick={() => {
                                    const loadingId = generateIdentificatorForLoadingAction();
                                    setBackendErrors(null);
                                    dispatch(app.actions.loading(loadingId));

                                    axios({
                                        url: `${process.env.REACT_APP_BACKEND_URL}/attached_files/general/${generalAttachedFile.id}`,
                                        method: 'GET', headers: {
                                            'Authorization': `Bearer ${getServiceToken()}`
                                        },
                                        responseType: 'blob'
                                    }).then((response) => {
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', generalAttachedFile.originalFilename);
                                        document.body.appendChild(link);
                                        link.click();
                                        closeModalWindow(resetForm);
                                        document.body.removeChild(link);
                                        dispatch(app.actions.loaded(loadingId));
                                    }).catch(error => {
                                        setFileDownloadError(true);
                                        dispatch(app.actions.loaded(loadingId));
                                    });
                                }}
                            >
                                <FormattedMessage id="project.elements.import.report.download" />
                            </button>}
                        {!fileDownloadError && !generalAttachedFile && <button id="submitButton" type="submit" className="btn btn-primary"
                            disabled={loading} form="importFromShapeForm"
                        >
                            <FormattedMessage id="project.app.Body.accept" />
                        </button>}
                        <button id="cancelButton" type="button" className="btn btn-danger"
                            disabled={loading}
                            onClick={() => closeModalWindow(resetForm)}
                        >
                            <FormattedMessage id="project.app.Body.cancel" />
                        </button>
                    </Modal.Footer>
                </Modal>}
        </Formik>
    );
}

export default connect(mapStateToProps)(injectIntl(ImportGeometricElementsFromShape));